import { CommonModule } from '@angular/common';
import { Component,Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import {
    MatLegacyDialogModule,
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule } from '@angular/material/legacy-list';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { AccountsReceivableLedgerTypeEnum,ARLTypeEnumNames,availableBookingPositionTypeEnums,isBookingPositionTypeEnum } from '../../../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { AppButtonModule } from '../../../../../dave-utils-module/app-button-module/app-button.module';
import { LengthPipe } from '../../../../../helper/length.pipe';
import {
    arlBaseCostAllowanceFormGroup,
    arlBaseCostAllowanceType,
    getARLBaseCostAllowanceForm,
} from '../../arl-form-data.service';
import { ArlChargeDialogBase } from '../arl-charge-dialog/ArlChargeDialogBase';
import { firstValueFrom, merge } from 'rxjs';

export type ArlChargeOverrideDialogComponentDialogReturnData = undefined | 'submitted';
export interface ArlChargeOverrideDialogComponentDialogData {
    arlChargeFormArray: FormArray<arlBaseCostAllowanceFormGroup>;
}

function filterAllowanceCharges(form: arlBaseCostAllowanceFormGroup): boolean {
    return !!form.value.arlType && form.value.type === 'surchargeOverride' && (form.value.percent || 0) >= 0; // todo kann ggf weg
}
@Component({
    selector: 'app-arl-charge-override-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FontAwesomeModule,
        MatLegacyFormFieldModule,
        FormsModule,
        MatLegacyInputModule,
        MatLegacyListModule,
        MatLegacyButtonModule,
        MatLegacyTooltipModule,
        ReactiveFormsModule,
        MatLegacyDialogModule,
        AppButtonModule,
        MatLegacySelectModule,
        LengthPipe,
    ],
    templateUrl: './arl-charge-override-dialog.component.html',
    styleUrls: ['./arl-charge-override-dialog.component.scss'],
})
export class ArlChargeOverrideDialogComponent extends ArlChargeDialogBase {
    protected filterAllowanceCharges(form: arlBaseCostAllowanceFormGroup): boolean {
        return filterAllowanceCharges(form); // todo kann ggf weg
    }
    constructor(@Inject(MAT_DIALOG_DATA) private dialogData: ArlChargeOverrideDialogComponentDialogData, private dialogRef: MatLegacyDialogRef<ArlChargeOverrideDialogComponent, ArlChargeOverrideDialogComponentDialogReturnData>) {
        const _arlChargeFormArray = new FormArray<arlBaseCostAllowanceFormGroup>(dialogData.arlChargeFormArray.controls.filter(filterAllowanceCharges).map<arlBaseCostAllowanceFormGroup>(c => new FormGroup({
            title: new FormControl<string>(c.controls.title.value),
            percent: new FormControl<number>(c.controls.percent.value),
            arlType: new FormControl<AccountsReceivableLedgerTypeEnum>(c.controls.arlType.value),
            type: new FormControl<arlBaseCostAllowanceType>(c.controls.type.value),
        })));
        super(_arlChargeFormArray);
    }
    protected typeOptions = availableBookingPositionTypeEnums.filter(isBookingPositionTypeEnum).map((q) => ({ value: q, label: ARLTypeEnumNames.get(q) }));
    protected filteredTypeOptions$ = this.shownArlAllowanceFormArray$.pipe(
        switchMap(form => merge(...form.map(f => f.valueChanges)).pipe(
            startWith(null),
            map(() => {
                const types = form.map((s) => s.getRawValue().arlType);
                return this.typeOptions.filter((type) => types.every((t) => t !== type.value));
            }),
        )),
        distinctUntilChanged((a, b) => a.length === b.length && JSON.stringify(a) === JSON.stringify(b)),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    async addRandom() {
        const options = await firstValueFrom(this.filteredTypeOptions$)
        if (options.length) {
            this.addByType(options[0].value);
        }
    }
    addByType(arlType: AccountsReceivableLedgerTypeEnum) {
        this.add(
            getARLBaseCostAllowanceForm(
                {
                    title: null,
                    percent: null,
                    arlType,
                },
                'surchargeOverride',
                true,
            ),
        );
    }

    save() {
        this.dialogData.arlChargeFormArray.controls.forEach((c, i) => {
            if (filterAllowanceCharges(c)) {
                const newControl = this.arlAllowanceFormArray.controls.find(f => f.getRawValue().type === c.getRawValue().type && f.getRawValue().arlType === c.getRawValue().arlType);
                if (newControl) {
                    c.markAsDirty() ;
                    c.setValue(newControl.getRawValue());
                } else {
                    this.dialogData.arlChargeFormArray.removeAt(i);
                }
            }
        });
        this.arlAllowanceFormArray.controls
            .filter(f => !this.dialogData.arlChargeFormArray.controls.some(c => f.getRawValue().type === c.getRawValue().type && f.getRawValue().arlType === c.getRawValue().arlType))
            .forEach(c => {
            this.dialogData.arlChargeFormArray.push(new FormGroup({
                title: new FormControl<string>(c.controls.title.value),
                percent: new FormControl<number>(c.controls.percent.value),
                arlType: new FormControl<AccountsReceivableLedgerTypeEnum>(c.controls.arlType.value),
                type: new FormControl<arlBaseCostAllowanceType>(c.controls.type.value),
            }));
        });
        this.dialogRef.close('submitted');
    }
    protected readonly ARLTypeEnumNames = ARLTypeEnumNames;
}
